<script setup lang="ts">
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import Timeline from '@/components/Timeline.vue'
import { Location, Step } from '@/models'
import ConfirmationMessage from '@/steps/ConfirmationMessage.vue'
import ErrorMessage from '@/steps/ErrorMessage.vue'
import Locations from '@/steps/Locations.vue'
import PostalCode from '@/steps/PostalCode.vue'
import UserData from '@/steps/UserData.vue'
import { usePadelStore } from '@/store/padel'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import { onMounted } from 'vue'

const props = defineProps<{ token?: string }>()

const store = usePadelStore()

onMounted(() => {
    if (props.token) {
        store.validateToken(props.token)
    }
})

const steps: Array<Step> = [Step.postalCode, Step.location, Step.userData, Step.confirmation]

const submitPostalCode = (postalCode: string) => {
    store.postalCode = postalCode
    store.retrieveLocations()
}

const submitLocation = (location: Location) => {
    store.location = location
}
const submitUserData = (firstName: string, lastName: string, email: string, phone: string, licensePlate: string) => {
    store.firstName = firstName
    store.lastName = lastName
    store.email = email
    store.phone = phone
    store.licensePlate = licensePlate
    store.requestDiscountCode()
}
</script>
<template>
    <div v-if="props.token && store.tokenValidating">
        <LoadingSpinner />
    </div>
    <div v-else-if="props.token && store.tokenValid === false" class="mt-1.5 mb-3">
        <div class="bg-cupra-danger-dark-theme rounded p-4">
            <div class="flex gap-4">
                <ExclamationTriangleIcon class="size-6 text-white" />
                <p>Deze link is niet (meer) geldig. Neem contact op als je denkt dat dit niet klopt.</p>
            </div>
        </div>
    </div>
    <template v-else>
        <Timeline
            class="mb-4"
            :steps="steps"
            :current-step="store.step"
        />

        <PostalCode
            v-if="store.step === Step.postalCode"
            @submit="submitPostalCode"
        >
            Vul je postcode in om padellocaties bij jou in de buurt te vinden.
        </PostalCode>
        <Locations
            v-else-if="store.step === Step.location && store.mapOptions && store.locations"
            @back="store.locations = undefined"
            @submit="submitLocation"
            :map-options="store.mapOptions"
            :locations="store.locations"
        >
            Kies een locatie in de buurt van
            <span class="font-medium">{{ store.formattedAddress }}</span
            >.
        </Locations>
        <UserData
            v-else-if="store.step === Step.userData"
            @back="store.location = undefined"
            @submit="submitUserData"
            :without-phone="true"
            :without-license-plate="!!props.token"
        >
            <p>
                Vul je gegevens in zodat we je een kortingscode kunnen sturen<template v-if="store.location">
                    voor <span class="font-medium">{{ store.location.name }}</span></template
                >.
            </p>
        </UserData>
        <ErrorMessage
            v-else-if="store.step === Step.error"
            @retry="store.requestCodeError = undefined"
        >
            {{ store.formattedRequestCodeError }}
        </ErrorMessage>
        <ConfirmationMessage v-else-if="store.step === Step.confirmation">Je ontvangt een e-mail met je kortingscode.</ConfirmationMessage>
        <LoadingSpinner v-else />
    </template>
</template>
